'use client';

import { Option } from '@app/types/data-table';
import { Icons } from '@shared/components/content/icons';
import Loader from '@shared/components/loader';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@shared/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover';
import { Separator } from '@shared/components/ui/separator';
import { cn, pluralise } from '@shared/lib/utils';
import * as React from 'react';
import { useFilterPillDensity } from './filter-pill-density';

interface FacetedFilterProps {
  title?: string;
  options: Option[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  multiple?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  /** Singular noun for the "Select N <itemName>s" row shown while searching */
  itemName?: string;
}

export function FacetedFilter({
  title,
  options,
  selectedValues,
  onChange,
  multiple,
  disabled,
  isLoading,
  itemName = 'option',
}: FacetedFilterProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const density = useFilterPillDensity();
  const maxNamed = density === 'compact' ? 1 : 2;
  const selected = React.useMemo(
    () => new Set(selectedValues),
    [selectedValues]
  );
  const selectedOptions = React.useMemo(
    () => options.filter((option) => selected.has(option.value)),
    [options, selected]
  );

  const onOpenChange = React.useCallback((next: boolean) => {
    setOpen(next);
    if (!next) setSearch('');
  }, []);

  const filteredOptions = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(term)
    );
  }, [options, search]);

  const showSelectMatching =
    !!multiple && !!search.trim() && filteredOptions.length > 1;

  const allMatchingSelected = filteredOptions.every((option) =>
    selected.has(option.value)
  );

  const onToggleMatching = React.useCallback(() => {
    const next = new Set(selected);
    filteredOptions.forEach((option) => {
      if (allMatchingSelected) next.delete(option.value);
      else next.add(option.value);
    });
    onChange(Array.from(next));
  }, [allMatchingSelected, filteredOptions, onChange, selected]);

  const onItemSelect = React.useCallback(
    (option: Option, isSelected: boolean) => {
      if (multiple) {
        const next = new Set(selected);
        if (isSelected) next.delete(option.value);
        else next.add(option.value);
        onChange(Array.from(next));
      } else {
        onChange(isSelected ? [] : [option.value]);
        setOpen(false);
      }
    },
    [multiple, onChange, selected]
  );

  const onReset = React.useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      onChange([]);
    },
    [onChange]
  );

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'border-dashed',
            // In a compact row the pill shrinks to the room it has and the
            // value badge takes what is left.
            density === 'compact' && 'min-w-0 max-w-full',
            selected.size > 0 &&
              'border-primary-600 bg-primary-600 font-semibold text-gray-00 shadow-sm hover:bg-primary-600 hover:text-gray-00'
          )}
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <Loader size="xs" className="mr-2 size-4" />
          ) : selected.size > 0 ? (
            <div
              role="button"
              aria-label={`Clear ${title} filter`}
              tabIndex={0}
              onClick={onReset}
              className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <Icons.xCircle className="mr-2 size-4" />
            </div>
          ) : (
            <Icons.plusCircle className="mr-2 size-4" />
          )}
          {title}
          {selected.size > 0 && (
            <>
              <Separator
                orientation="vertical"
                className="mx-2 data-[orientation=vertical]:h-4"
              />
              <Badge
                variant="secondary"
                className={cn(
                  'rounded-sm px-1 font-normal lg:hidden',
                  'border-transparent bg-primary-500 text-gray-00'
                )}
              >
                {selected.size}
              </Badge>
              <div className="hidden min-w-0 items-center gap-1 lg:flex">
                {/* A value the options don't name (a link to something the
                    list no longer offers) still counts, so the pill never
                    reads as set with nothing after it. */}
                {selected.size > maxNamed || selectedOptions.length === 0 ? (
                  <Badge
                    variant="secondary"
                    className={cn(
                      'rounded-sm px-1 font-normal',
                      'border-transparent bg-primary-500 text-gray-00'
                    )}
                  >
                    {selected.size} selected
                  </Badge>
                ) : (
                  selectedOptions.map((option) => (
                    <Badge
                      variant="secondary"
                      key={option.value}
                      className={cn(
                        'rounded-sm px-1 font-normal',
                        'border-transparent bg-primary-500 text-gray-00',
                        density === 'compact' && 'block min-w-0 truncate'
                      )}
                    >
                      {option.label}
                    </Badge>
                  ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full min-w-[12.5rem] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={title}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-full">
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup className="max-h-[18.75rem] overflow-y-auto overflow-x-hidden">
              {filteredOptions.map((option) => {
                const isSelected = selected.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => onItemSelect(option, isSelected)}
                  >
                    <div
                      className={cn(
                        'flex mr-2 size-4 items-center justify-center rounded-sm border border-primary',
                        isSelected
                          ? 'bg-primary'
                          : 'opacity-50 [&_svg]:invisible'
                      )}
                    >
                      <Icons.check className="text-gray-00" />
                    </div>
                    {option.icon && <option.icon />}
                    <span className="truncate">{option.label}</span>
                    {option.count && (
                      <span className="ml-auto font-mono text-xs">
                        {option.count}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {(showSelectMatching || selected.size > 0) && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  {showSelectMatching ? (
                    <CommandItem
                      onSelect={onToggleMatching}
                      className="justify-center text-center"
                    >
                      {allMatchingSelected ? 'Deselect' : 'Select'}{' '}
                      {filteredOptions.length} {pluralise(itemName)}
                    </CommandItem>
                  ) : (
                    <CommandItem
                      onSelect={() => onReset()}
                      className="justify-center text-center"
                    >
                      Clear selected
                    </CommandItem>
                  )}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
